import { haystackMatchesCommaSearch } from "@/lib/manufacturing/commaDelimitedSearch";
import type { MfgLaborBay, MfgLaborRateItem } from "@/lib/manufacturing/laborBillingTypes";

export type LaborRateActiveFilter = "all" | "active" | "inactive";
export type LaborRateRoofFilter = "all" | "roof" | "not_roof";
/** `all` = any bay assignment; `unlinked` = no earning bay; else a bay id. */
export type LaborRateBayFilter = "all" | "unlinked" | string;

export type LaborRateCatalogQuery = {
  search: string;
  activeFilter: LaborRateActiveFilter;
  roofFilter: LaborRateRoofFilter;
  bayFilter: LaborRateBayFilter;
};

export const DEFAULT_LABOR_RATE_CATALOG_QUERY: LaborRateCatalogQuery = {
  search: "",
  activeFilter: "all",
  roofFilter: "all",
  bayFilter: "all",
};

export function laborRateCatalogQueryIsDefault(query: LaborRateCatalogQuery): boolean {
  return (
    query.search.trim() === "" &&
    query.activeFilter === "all" &&
    query.roofFilter === "all" &&
    query.bayFilter === "all"
  );
}

export function filterLaborRateCatalog(
  items: readonly MfgLaborRateItem[],
  opts: LaborRateCatalogQuery & {
    linksByItem: Readonly<Record<string, readonly string[]>>;
    bays?: readonly MfgLaborBay[];
  },
): MfgLaborRateItem[] {
  const bayById = new Map((opts.bays ?? []).map((bay) => [bay.id, bay]));

  return items.filter((item) => {
    if (opts.activeFilter === "active" && !item.is_active) return false;
    if (opts.activeFilter === "inactive" && item.is_active) return false;
    if (opts.roofFilter === "roof" && !item.roof_eligible) return false;
    if (opts.roofFilter === "not_roof" && item.roof_eligible) return false;

    const linked = opts.linksByItem[item.id] ?? [];
    if (opts.bayFilter === "unlinked") {
      if (linked.length > 0) return false;
    } else if (opts.bayFilter !== "all") {
      if (!linked.includes(opts.bayFilter)) return false;
    }

    const bayHay = linked
      .map((id) => bayById.get(id))
      .filter((bay): bay is MfgLaborBay => Boolean(bay))
      .map((bay) => `${bay.name} ${bay.bay_code}`)
      .join(" ");
    const hay = [item.label, item.item_code, item.option_code, item.notes, bayHay].join(" ");
    return haystackMatchesCommaSearch(hay, opts.search);
  });
}
